import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumbTrail, type TrailCrumb } from '../context/BreadcrumbTrailContext';
import { colors } from '../constants/colors';
import { fontSize, fontWeight } from '../constants/typography';
import type {
  ContentBreadcrumbItem,
  ContentBreadcrumbsProps,
} from '../screens/admin/types';
import { launchpadStrings } from './StringBundle';

const breadcrumbStrings = launchpadStrings.breadcrumb;

export type { ContentBreadcrumbItem };

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '8px 14px',
    backgroundColor: colors.white,
    borderRadius: '8px',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    width: '100%',
    boxSizing: 'border-box',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    [theme.breakpoints.down('sm')]: {
      padding: '8px 10px',
    },
    '&::-webkit-scrollbar': {
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: colors.scrollbarThumb,
      borderRadius: 3,
    },
    scrollbarWidth: 'thin',
    '& .MuiBreadcrumbs-ol': {
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      alignItems: 'center',
    },
    '& .MuiBreadcrumbs-li': {
      whiteSpace: 'nowrap',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiBreadcrumbs-separator': {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
    },
  },
  sticky: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
  },
  link: {
    color: colors.orange,
    fontSize: `${fontSize.breadcrumb} !important`,
    lineHeight: 1,
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  inactive: {
    fontSize: `${fontSize.breadcrumb} !important`,
    lineHeight: 1,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
  },
  current: {
    fontSize: `${fontSize.breadcrumb} !important`,
    lineHeight: 1,
    fontWeight: fontWeight.semiBold,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
  },
}));

export function ContentBreadcrumbs({ items, onNavigate, sticky }: ContentBreadcrumbsProps) {
  const classes = useStyles();

  const handleLinkClick = (e: React.MouseEvent, item: ContentBreadcrumbItem): void => {
    e.preventDefault();
    if (onNavigate) onNavigate(item);
  };

  return (
    <div className={sticky ? `${classes.root} ${classes.sticky}` : classes.root}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label={breadcrumbStrings.navAriaLabel}
      >
        {items?.map((item, index) => {
          const isLast = index === items.length - 1;
          if (isLast) {
            return (
              <Typography key={index} className={classes.current}>
                {item.label}
              </Typography>
            );
          }
          if (item.to) {
            return (
              <Link
                key={index}
                className={classes.link}
                onClick={(e: React.MouseEvent) => handleLinkClick(e, item)}
              >
                {item.label}
              </Link>
            );
          }
          return (
            <Typography key={index} className={classes.inactive}>
              {item.label}
            </Typography>
          );
        })}
      </Breadcrumbs>
    </div>
  );
}

function collapseTrail(items: TrailCrumb[]): TrailCrumb[] {
  if (!Array.isArray(items) || items.length <= 1) return items;
  const lastIndex = items.length - 1;
  return items?.filter(
    (c, i) => i === 0 || i === lastIndex || c.kind !== 'page',
  );
}

export default function BreadcrumbBar() {
  const breadcrumbs = useBreadcrumbTrail();
  const navigate = useNavigate();

  // Strip department-only crumbs (deptKey crumbs) -- show only page-level crumbs.
  const pageCrumbs = breadcrumbs.filter((item) => !item.deptKey);
  const displayCrumbs = collapseTrail(pageCrumbs);

  if (!displayCrumbs || displayCrumbs.length < 1) return null;

  const handleNavigate = (item: ContentBreadcrumbItem): void => {
    const crumb = item as TrailCrumb;
    if (!crumb.to) return;
    // Pass __breadcrumbNav so the trail context can truncate everything after this crumb.
    const baseState = crumb.state && Object.keys(crumb.state).length > 0 ? crumb.state : {};
    navigate(crumb.to, { state: { ...baseState, __breadcrumbNav: true } });
  };

  return (
    <ContentBreadcrumbs
      items={displayCrumbs}
      onNavigate={handleNavigate}
      sticky
    />
  );
}
